import React, { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useSubscriptions from "@/hooks/useSubscriptions";

interface ScrubberPattern {
  name: string;
  description: string;
  replacement: string;
  contextRequired: boolean;
}

interface ScrubberData {
  enabled: boolean;
  patterns: ScrubberPattern[];
}

export default function MessageScrubberPanel() {
  const api = useApi();
  const { subscription } = useSubscriptions();
  const [data, setData] = useState<ScrubberData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const resp = await api.messaging.get("admin/scrubber");
      if (resp.ok) {
        setData(await resp.json());
      }
    } catch {
      // Graceful — show defaults
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const hasScrubbing = subscription?.enableMessageScrubbing ?? false;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cornflower-blue-500" />
        <span className="ml-3 text-slate-gray-500 text-sm">Loading scrubber settings...</span>
      </div>
    );
  }

  if (!hasScrubbing) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div>
          <h2 className="text-xl font-bold text-slate-gray-800">Message Scrubbing</h2>
          <p className="text-sm text-slate-gray-500 mt-1">
            Automatically detect and redact sensitive data (credit cards, SSNs, etc.) from chat messages in real time.
          </p>
        </div>
        <div className="mt-6 bg-slate-gray-50 rounded-lg border border-slate-gray-200 p-5 text-sm text-slate-gray-600">
          <p className="font-medium text-slate-gray-700">Message scrubbing is not included in your current plan</p>
          <p className="mt-1">
            Upgrade to enable automatic PII detection and redaction. Contact sales@velaro.com for pricing.
          </p>
        </div>
      </div>
    );
  }

  const patterns = Array.isArray(data?.patterns) ? data!.patterns : [];
  const isEnabled = data?.enabled ?? false;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-slate-gray-800">Message Scrubbing</h2>
        <p className="text-sm text-slate-gray-500 mt-1">
          Automatically detect and redact sensitive data from chat messages in real time.
        </p>
      </div>

      {/* Status */}
      <div className={`rounded-lg border p-4 flex items-center gap-3 ${
        isEnabled
          ? "border-emerald-200 bg-emerald-50"
          : "border-amber-200 bg-amber-50"
      }`}>
        <span className={`w-3 h-3 rounded-full ${isEnabled ? "bg-emerald-500" : "bg-amber-500"}`} />
        <div>
          <span className={`text-sm font-semibold ${isEnabled ? "text-emerald-800" : "text-amber-800"}`}>
            {isEnabled ? "Active" : "Inactive"}
          </span>
          <span className={`text-sm ml-2 ${isEnabled ? "text-emerald-600" : "text-amber-600"}`}>
            {isEnabled
              ? "Messages are being scanned for sensitive data in real time."
              : "Scrubbing is enabled on your plan but not yet active for this site. Contact your administrator."}
          </span>
        </div>
      </div>

      {/* Built-in Patterns */}
      <div>
        <h3 className="font-semibold text-slate-gray-800 mb-3">Built-in Detection Patterns</h3>
        <p className="text-sm text-slate-gray-500 mb-4">
          These patterns run automatically on every incoming and outgoing message. They cannot be customized yet.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {patterns.map((pattern, i) => (
            <PatternCard key={i} pattern={pattern} />
          ))}
        </div>
      </div>

      {/* How it works */}
      <div className="bg-cornflower-blue-50 border border-cornflower-blue-200 rounded-lg p-5">
        <h3 className="font-semibold text-cornflower-blue-800 mb-2">How it works</h3>
        <ul className="text-sm text-cornflower-blue-700 space-y-1.5">
          <li className="flex items-start gap-2">
            <span className="text-cornflower-blue-500 font-bold mt-0.5">1.</span>
            <span>When a visitor or agent sends a message, the scrubber checks it against all active patterns.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cornflower-blue-500 font-bold mt-0.5">2.</span>
            <span>Matched sensitive data is replaced with a redaction tag (e.g., [redacted card]) before the message is stored or displayed.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cornflower-blue-500 font-bold mt-0.5">3.</span>
            <span>The original sensitive data is never stored in the database or shown to agents.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cornflower-blue-500 font-bold mt-0.5">4.</span>
            <span>Detections are logged for compliance auditing (without the actual sensitive values).</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

function PatternCard({ pattern }: { pattern: ScrubberPattern }) {
  return (
    <div className="bg-white rounded-lg border border-slate-gray-200 p-4">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm font-semibold text-slate-gray-800">{pattern.name}</span>
        {pattern.contextRequired && (
          <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
            Context-aware
          </span>
        )}
      </div>
      <p className="text-xs text-slate-gray-500 mb-2">{pattern.description}</p>
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-slate-gray-400">Replaced with:</span>
        <code className="text-xs bg-slate-gray-100 text-crimson-red-600 px-1.5 py-0.5 rounded">
          {pattern.replacement}
        </code>
      </div>
    </div>
  );
}
